import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
var _excluded = ["classes", "children", "value", "size", "selected", "disabled"];
import React from 'react';
import PropTypes from 'prop-types';
import { default as MuiToggleButton } from '@material-ui/lab/ToggleButton';
import { withStyles } from '../styles';
import { colorBrandNeutral200, colorBrandNeutral250, colorBrandNeutral300, colorBrandNeutral400, colorBrandNeutral500, colorBrandNeutral600, borderWidthThick } from '../styles/tokens';
export var styles = function styles(theme) {
  return {
    root: {
      border: 'none',
      '&:hover': {
        backgroundColor: colorBrandNeutral200,
        borderColor: 'none'
      },
      '&:focus': {
        boxShadow: "0 0 0 " + borderWidthThick + " " + theme.palette.focus,
        '& svg': {
          fill: colorBrandNeutral600
        }
      },
      '&:disabled': {
        backgroundColor: colorBrandNeutral250,
        '& svg': {
          fill: colorBrandNeutral400
        }
      },
      '&$selected': {
        backgroundColor: colorBrandNeutral300,
        '& svg': {
          fill: colorBrandNeutral600
        },
        '&:hover': {
          backgroundColor: colorBrandNeutral200,
          borderColor: 'none'
        },
        '&:focus': {
          boxShadow: "0 0 0 " + borderWidthThick + " " + theme.palette.focus
        },
        '&:disabled': {
          backgroundColor: colorBrandNeutral250,
          '& svg': {
            fill: colorBrandNeutral400
          }
        }
      }
    },
    label: {
      color: colorBrandNeutral500,
      '& svg': {
        fill: colorBrandNeutral500
      }
    },
    selected: {}
  };
};
/**
 * Toggle buttons can be used to group related options.
 * @done true
 * @updated v5.4.0
 * @versionAdded v5.3.0
 * @examples
 *  ToggleButtonDefaultExample
 *  ToggleButtonGroupExample
 */

var ToggleButton = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var classes = props.classes,
      children = props.children,
      value = props.value,
      size = props.size,
      selected = props.selected,
      disabled = props.disabled,
      rest = _objectWithoutPropertiesLoose(props, _excluded);

  var classOverrides = {
    root: classes.root,
    label: classes.label,
    selected: classes.selected
  };
  return /*#__PURE__*/React.createElement(MuiToggleButton, _extends({
    value: value,
    disableFocusRipple: true,
    disableRipple: true,
    classes: classOverrides,
    size: size,
    selected: selected,
    disabled: disabled
  }, rest), children);
});
ToggleButton.muiName = 'ToggleButton';
ToggleButton.displayName = 'ToggleButton';
ToggleButton.propTypes = {
  /**
  * Class name to assign the component.
  */
  className: PropTypes.string,

  /**
   * Classes used to extend original style object.
   */
  classes: PropTypes.object,

  /**
   * The content of the button.
   */
  children: PropTypes.node.isRequired,

  /**
   * If `true`, the button will be disabled.
   */
  disabled: PropTypes.bool,

  /**
   * Callback fired when the value changes.
   *
   * **Signature:**
   * ```
   * function(event: object, value: any) => void
   * ```
   * `event`: The event source of the callback.
   *
   * `value`: of the button.
   */
  onChange: PropTypes.func,

  /**
   * If `true`, the button will be rendered in an active state.
   */
  selected: PropTypes.bool,

  /**
   * The size of the button.
   */
  size: PropTypes.oneOf(['large', 'medium', 'small']),

  /**
   * The value to associate with the button when selected.
   */
  value: PropTypes.any
};
ToggleButton.defaultProps = {
  size: 'medium',
  disabled: false
};
export default withStyles(styles)(ToggleButton);